import Router from 'next/router';
import { useCallback, useContext } from 'react';

import { useTrackAssetModalClosed } from '@air/analytics';

import { getQueryWithNullAssetModalParams } from '~/components/AssetModalV2/utils';
import {
  WorkspaceAgentPanelSessionContext,
  workspaceAgentPanelDefaultSessionValue,
} from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { pushWithExistingQuery } from '~/utils/PathUtils';

const readRouterQuerySessionId = (): string | undefined => {
  try {
    const queryValue = Router.query?.[QueryParamNames.chatId] ?? Router.query?.sessionId;

    if (typeof queryValue === 'string' && queryValue.length > 0) {
      return queryValue;
    }
  } catch {
    // Router singleton is not initialized on SSR or before mount in component tests.
  }

  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);

    return params.get(QueryParamNames.chatId) || params.get('sessionId') || undefined;
  }

  return undefined;
};

const resolveOpenAssetListTargetPath = ({
  artifactId,
  isAgentRoute,
  isAssetModalOpen,
  pathname,
  sessionId,
}: {
  artifactId: string;
  isAgentRoute: boolean;
  isAssetModalOpen: boolean;
  pathname: string;
  sessionId: string;
}): string | undefined => {
  if (isAgentRoute) {
    return `/agent/${sessionId}/artifacts/${artifactId}`;
  }
  if (isAssetModalOpen) {
    return pathname.split('/c/')[0];
  }

  return undefined;
};

type UseOpenWorkspaceAgentAssetListReturn = {
  closeAssetListTab: (artifactId: string) => void;
  closeAssetListView: () => void;
  /** Undefined when there is no session, so callers do not offer a click that would do nothing. */
  openAssetList: ((artifactId: string, title?: string) => void) | undefined;
};

/**
 * Opens an agent asset list artifact in the inline takeover view beside the docked chat, syncing
 * `artifactId` and `chatId` in the URL query while keeping the current page mounted underneath.
 * An open asset modal is closed in that same navigation, so the list replaces the modal instead of
 * stacking on it.
 */
export const useOpenWorkspaceAgentAssetList = (): UseOpenWorkspaceAgentAssetListReturn => {
  const { trackAssetModalClosed } = useTrackAssetModalClosed();
  const panelSession = useContext(WorkspaceAgentPanelSessionContext);
  const panelSessionId =
    panelSession === workspaceAgentPanelDefaultSessionValue ? undefined : (panelSession.sessionId ?? undefined);
  const storeSessionId = useWorkspaceAgentAssetListViewStore((state) => state.activeSessionId);
  const querySessionId = readRouterQuerySessionId();
  const sessionId = panelSessionId ?? storeSessionId ?? querySessionId;
  const openArtifactTab = useWorkspaceAgentAssetListViewStore((state) => state.openArtifactTab);
  const closeArtifactTab = useWorkspaceAgentAssetListViewStore((state) => state.closeArtifactTab);
  const closeArtifactView = useWorkspaceAgentAssetListViewStore((state) => state.closeArtifactView);

  const openAssetList = useCallback(
    (artifactId: string, title?: string) => {
      if (!sessionId) {
        return;
      }

      openArtifactTab({ artifactId, reopen: true, sessionId, title });
      useWorkspaceAgentPanelStore.getState().dockToRight();

      const pathname = Router.asPath.split('?')[0] ?? Router.asPath;
      const isAssetModalOpen = pathname.includes('/c/');
      const isAgentRoute = Router.pathname.startsWith('/agent/');
      const targetPath = resolveOpenAssetListTargetPath({
        artifactId,
        isAgentRoute,
        isAssetModalOpen,
        pathname,
        sessionId,
      });

      void pushWithExistingQuery({
        ...(targetPath !== undefined ? { path: targetPath } : {}),
        newQuery: {
          ...(isAssetModalOpen ? getQueryWithNullAssetModalParams() : {}),
          [QueryParamNames.chatId]: sessionId,
          [QueryParamNames.artifactId]: artifactId,
        },
      });

      if (!isAssetModalOpen) {
        return;
      }

      useWorkspaceRightPanelStore.getState().setCanvasReturnTo(null);
      trackAssetModalClosed({ location: 'workspace-agent-asset-list' });
    },
    [openArtifactTab, sessionId, trackAssetModalClosed],
  );

  const closeAssetListView = useCallback(() => {
    closeArtifactView();

    if (Router.pathname.startsWith('/agent/')) {
      void Router.push({
        pathname: Routes.generalLibrary,
        query: sessionId ? { [QueryParamNames.chatId]: sessionId } : {},
      });
      return;
    }

    void pushWithExistingQuery({
      newQuery: {
        [QueryParamNames.artifactId]: null,
      },
    });
  }, [closeArtifactView, sessionId]);

  const closeAssetListTab = useCallback(
    (artifactId: string) => {
      const targetSessionId = sessionId ?? useWorkspaceAgentAssetListViewStore.getState().activeSessionId;

      if (!targetSessionId) {
        closeAssetListView();
        return;
      }

      const nextArtifactId = closeArtifactTab(targetSessionId, artifactId);

      if (!nextArtifactId) {
        if (Router.pathname.startsWith('/agent/')) {
          void Router.push({
            pathname: Routes.generalLibrary,
            query: { [QueryParamNames.chatId]: targetSessionId },
          });
          return;
        }

        void pushWithExistingQuery({
          newQuery: {
            [QueryParamNames.artifactId]: null,
          },
        });
        return;
      }

      if (Router.pathname.startsWith('/agent/')) {
        void Router.push({
          pathname: `/agent/${targetSessionId}/artifacts/${nextArtifactId}`,
          query: {
            [QueryParamNames.chatId]: targetSessionId,
            [QueryParamNames.artifactId]: nextArtifactId,
          },
        });
        return;
      }

      void pushWithExistingQuery({
        newQuery: {
          [QueryParamNames.chatId]: targetSessionId,
          [QueryParamNames.artifactId]: nextArtifactId,
        },
      });
    },
    [closeArtifactTab, closeAssetListView, sessionId],
  );

  return {
    closeAssetListTab,
    closeAssetListView,
    openAssetList: sessionId ? openAssetList : undefined,
  };
};
